/**
 * HTML → WhatsApp plain text, for a rendered document template loaded into a WhatsApp composer.
 * Keeps link targets, block/row structure and decodes entities; drops head, styles, scripts
 * and images. Same output as messaging-hub's htmlToWhatsAppText, which converts html-only sends.
 */

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  bull: "•",
  middot: "·",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  laquo: "«",
  raquo: "»",
  copy: "©",
  reg: "®",
  trade: "™",
  euro: "€",
  pound: "£",
  yen: "¥",
  cent: "¢",
  times: "×",
  divide: "÷",
  deg: "°",
  shy: "",
  zwnj: "",
  zwj: "",
  lrm: "",
  rlm: "",
};

const CELL = "\u0001";
/** Paragraph end: a blank line. Other block boundaries are a single line break. */
const PARA = "\u0002";
const BR = "\u0003";

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
    if (entity[0] === "#") {
      const code =
        entity[1] === "x" || entity[1] === "X"
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);
      return Number.isFinite(code) && code >= 0x20 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : match;
    }
    const named = NAMED_ENTITIES[entity.toLowerCase()];
    return named === undefined ? match : named;
  });
}

function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

/** Output stays entity-encoded: the whole text is decoded once at the end. */
function linkText(inner: string, rawHref: string): string {
  const trimmedHref = rawHref.trim();
  const href = decodeEntities(trimmedHref);
  if (!/^(https?:|mailto:|tel:)/i.test(href)) return inner;
  const target = href.replace(/^(mailto:|tel:)/i, "");
  const label = decodeEntities(stripTags(inner)).replace(/\s+/g, " ").trim();
  const rawTarget = trimmedHref.replace(/^(mailto:|tel:)/i, "");
  if (!label) return rawTarget;
  if (label === target || label === href) return inner;
  return `${inner} (${rawTarget})`;
}

export function htmlToWhatsAppText(html: string | null | undefined): string {
  if (!html || !html.trim()) return "";

  let text = html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<(head|style|script|title|noscript|template)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "")
    // Source formatting whitespace is not content in HTML.
    .replace(/\s+/g, " ");

  text = text.replace(
    /<a\b[^>]*?\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a\s*>/gi,
    (_m, dq: string | undefined, sq: string | undefined, bare: string | undefined, inner: string) =>
      linkText(inner, dq ?? sq ?? bare ?? "")
  );

  text = text
    .replace(/<br\b[^>]*>/gi, `${BR}\n`)
    .replace(/<li\b[^>]*>/gi, "\n• ")
    .replace(/<\/(td|th)\s*>/gi, CELL)
    .replace(/<\/(p|h[1-6]|table|ul|ol|blockquote|pre|dl|figure)\s*>/gi, `\n${PARA}\n`)
    .replace(
      /<\/?(p|div|h[1-6]|table|thead|tbody|tfoot|tr|ul|ol|li|blockquote|section|article|header|footer|main|nav|center|hr|pre|address|dl|dt|dd|figure|figcaption)\b[^>]*>/gi,
      "\n"
    );

  text = decodeEntities(stripTags(text));

  const out: string[] = [];
  let gap = 0;
  for (const raw of text.split("\n")) {
    if (raw.includes(PARA)) {
      gap = 2;
      continue;
    }
    const line = raw
      .split(BR)
      .join("")
      .split(CELL)
      .map((cell) => cell.replace(/[ \t\u00a0]+/g, " ").trim())
      .filter(Boolean)
      .join(" | ");
    if (!line) {
      // An empty line made by <br> is an intended blank line.
      gap = raw.includes(BR) ? 2 : Math.max(gap, 1);
      continue;
    }
    if (out.length) out.push("\n".repeat(Math.max(gap, 1)));
    out.push(line);
    gap = 0;
  }
  return out.join("");
}
